// src/pages/SovereignEdgeTopology.jsx
// AISentry™ — Autonomous Sovereign Edge™
// Parameterized topology: medical, dc, print sectors
// All NGFW references removed — SASE architecture only

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// COLORS
// ════════════════════════════════════════════════════════════════════════════
const COLORS = {
  celerway: '#FF6B35',
  celerwayLight: 'rgba(255, 107, 53, 0.15)',
  paloAlto: '#FA582D',
  paloAltoDark: '#C74634',
  sase: '#FA582D',
  // Sector accent colors
  medical: '#8B5CF6',
  medicalLight: 'rgba(139, 92, 246, 0.15)',
  dc: '#F59E0B',
  dcLight: 'rgba(245, 158, 11, 0.15)',
  print: '#0096D6',
  printLight: 'rgba(0, 150, 214, 0.15)',
  secure: '#22C55E',
  secureLight: 'rgba(34, 197, 94, 0.15)',
  blocked: '#EF4444',
  blockedLight: 'rgba(239, 68, 68, 0.15)',
  dark: '#1E293B',
  darkAlt: '#0F172A',
  slate: '#64748B',
  white: '#FFFFFF',
};

// ════════════════════════════════════════════════════════════════════════════
// SECTOR CONFIGURATIONS
// ════════════════════════════════════════════════════════════════════════════
const SECTOR_CONFIGS = {
  medical: {
    title: 'AISentry™ — Autonomous Sovereign Edge™',
    subtitle: 'Celerway Xm + Palo Alto SASE • Network-Isolated Healthcare Connectivity',
    badges: ['Supports HIPAA Safeguards', 'Zero Trust', 'Threat Prevention'],
    badgeColors: ['orange', 'emerald', 'red'],
    accentColor: COLORS.medical,
    accentLight: COLORS.medicalLight,
    // OEM Cloud config
    oemCloud: {
      name: 'OEM Device Cloud',
      subtitle: 'Remote Service Platform',
      icon: '🏥',
      color: '#8B5CF6',
      colorLight: 'rgba(139, 92, 246, 0.15)',
      gradientFrom: 'from-violet-600',
      gradientTo: 'to-purple-600',
      bgFrom: 'from-violet-950',
      borderColor: 'border-violet-500/40',
      services: [
        { name: 'Remote Diagnostics', icon: '🔧' },
        { name: 'Predictive Maintenance', icon: '📊' },
        { name: 'Software Updates', icon: '⬆️' },
      ],
      badge: { text: 'Service Contract Active', icon: '✓' },
      stats: [
        { label: 'Devices', value: '847' },
        { label: 'Online', value: '100%' },
      ],
    },
    // Device config
    device: {
      type: 'medical',
      brand: 'GE HEALTHCARE',
      brandColor: '#8B5CF6',
      model: 'Voluson E10 Ultrasound',
      location: 'Imaging Suite 3',
      facility: 'Austin Diagnostic Center • Austin, TX',
      metrics: [
        { label: 'Uptime', value: '99.9%' },
        { label: 'Scans Today', value: '34' },
      ],
      complianceBadge: 'HIPAA Isolated',
      complianceColor: 'emerald',
      icon: 'medical',
    },
    // Facility LAN config
    facilityLAN: {
      name: 'Facility LAN',
      subtitle: 'Imaging Center IT Network',
      status: 'NO ACCESS • ISOLATED',
    },
    // Air gap text
    airGap: {
      title: 'Network-Isolated from Facility LAN',
      subtitle: 'Patient data never touches local network',
    },
    // Gateway footer
    gatewayFooter: {
      location: 'Austin Diagnostic Center • Austin, TX',
      serial: 'X2M-ATX-00847',
    },
    // Stats bar
    stats: [
      { label: 'Security', value: '100%', color: 'text-emerald-400' },
      { label: 'Threats Blocked', value: '2,847', color: 'text-red-400' },
      { label: 'Uptime', value: '99.99%', color: 'text-cyan-400' },
      { label: 'Data Protected', value: '12.4TB', color: 'text-orange-400', sub: 'telemetry' },
    ],
    // Value props
    valueProps: [
      'Device never touches facility LAN',
      'Palo Alto SASE at the edge',
      'Day 1 HIPAA compliance',
      'OEM owns security posture',
    ],
    // Edge labels
    oemEdgeLabel: 'Device Telemetry',
    deviceEdgeLabel: 'Encrypted • HIPAA',
    // Legend
    legend: [
      { label: 'Security (Palo Alto)', type: 'dashed', color: COLORS.paloAlto },
      { label: 'OEM Telemetry', type: 'solid', color: '#8B5CF6' },
      { label: 'Encrypted Link', type: 'solid', color: COLORS.secure },
      { label: 'Blocked (Air-Gap)', type: 'faded', color: COLORS.blocked },
    ],
  },

  print: {
    title: 'AISentry™ — Autonomous Sovereign Edge™',
    subtitle: 'Celerway Xm + Palo Alto SASE • Network-Isolated Fleet Connectivity',
    badges: ['Supports HIPAA Safeguards', 'Zero Trust', 'Threat Prevention'],
    badgeColors: ['orange', 'emerald', 'red'],
    accentColor: COLORS.print,
    accentLight: COLORS.printLight,
    oemCloud: {
      name: 'Fleet Management Cloud',
      subtitle: 'Smart Device Services',
      icon: '🖨️',
      color: '#0096D6',
      colorLight: 'rgba(0, 150, 214, 0.15)',
      gradientFrom: 'from-cyan-600',
      gradientTo: 'to-blue-600',
      bgFrom: 'from-cyan-950',
      borderColor: 'border-cyan-500/40',
      services: [
        { name: 'Fleet Management', icon: '🖨️' },
        { name: 'Supply Auto-Order', icon: '📦' },
        { name: 'Remote Diagnostics', icon: '🔧' },
      ],
      badge: { text: 'Healthcare Managed Services', icon: '🏥' },
      stats: [
        { label: 'Fleet Size', value: '4,247' },
        { label: 'Online', value: '100%' },
      ],
    },
    device: {
      type: 'printer',
      brand: 'HP ENTERPRISE',
      brandColor: '#0096D6',
      model: 'HP LaserJet Enterprise M635',
      location: 'Radiology Department',
      facility: 'Methodist Hospital • Austin, TX',
      metrics: [
        { label: 'Toner', value: '78%' },
        { label: 'Uptime', value: '99.9%' },
      ],
      complianceBadge: 'HIPAA Isolated',
      complianceColor: 'emerald',
      icon: 'printer',
    },
    facilityLAN: {
      name: 'Hospital LAN',
      subtitle: 'Methodist IT Network',
      status: 'NO ACCESS • ISOLATED',
    },
    airGap: {
      title: 'Network-Isolated from Hospital LAN',
      subtitle: 'PHI never touches local network',
    },
    gatewayFooter: {
      location: 'Methodist Hospital • Austin, TX',
      serial: 'X2M-ATX-00847',
    },
    stats: [
      { label: 'Security', value: '100%', color: 'text-emerald-400' },
      { label: 'Threats Blocked', value: '2,847', color: 'text-red-400' },
      { label: 'Uptime', value: '99.99%', color: 'text-cyan-400' },
      { label: 'PHI Protected', value: '847K', color: 'text-orange-400', sub: 'pages' },
    ],
    valueProps: [
      'Device never touches hospital LAN',
      'Palo Alto SASE at the edge',
      'Day 1 HIPAA compliance',
      'OEM owns security posture',
    ],
    oemEdgeLabel: 'Fleet Telemetry',
    deviceEdgeLabel: 'Encrypted • HIPAA',
    legend: [
      { label: 'Security (Palo Alto)', type: 'dashed', color: COLORS.paloAlto },
      { label: 'Fleet Mgmt', type: 'solid', color: '#0096D6' },
      { label: 'Encrypted Link', type: 'solid', color: COLORS.secure },
      { label: 'Blocked (Air-Gap)', type: 'faded', color: COLORS.blocked },
    ],
  },

  dc: {
    title: 'AISentry™ — Autonomous Sovereign Edge™',
    subtitle: 'Celerway Xm + Palo Alto SASE • Data Center Cyber-Physical Intelligence',
    badges: ['OT + OOB Monitoring', 'Zero Trust', 'Correlation Engine'],
    badgeColors: ['amber', 'emerald', 'purple'],
    accentColor: COLORS.dc,
    accentLight: COLORS.dcLight,
    oemCloud: {
      name: 'AISentry™ Cloud',
      subtitle: 'Correlation Engine + AI Analytics',
      icon: '🛡️',
      color: '#F59E0B',
      colorLight: 'rgba(245, 158, 11, 0.15)',
      gradientFrom: 'from-amber-600',
      gradientTo: 'to-orange-600',
      bgFrom: 'from-amber-950',
      borderColor: 'border-amber-500/40',
      services: [
        { name: 'DC Correlation Engine', icon: '🔗' },
        { name: 'AI Threat Assessment', icon: '🤖' },
        { name: 'Compliance Reporting', icon: '📋' },
      ],
      badge: { text: 'CORR-001 / CORR-002 / CORR-003 Active', icon: '⚡' },
      stats: [
        { label: 'Agents', value: '3' },
        { label: 'Detections', value: '364' },
      ],
    },
    device: {
      type: 'dc',
      brand: 'FACILITIES + OOB',
      brandColor: '#F59E0B',
      model: 'Dual-Agent Monitoring',
      location: 'Facilities VLAN + Management VLAN',
      facility: 'Data Center • Pod A',
      metrics: [
        { label: 'OT Devices', value: '12' },
        { label: 'BMCs', value: '48' },
      ],
      complianceBadge: 'SOC Analyst in a Box',
      complianceColor: 'amber',
      icon: 'datacenter',
    },
    facilityLAN: {
      name: 'Enterprise Network',
      subtitle: 'Production Server VLAN',
      status: 'ISOLATED • AIR-GAPPED',
    },
    airGap: {
      title: 'Air-Gapped from Enterprise Network',
      subtitle: 'OT and OOB traffic never touches production',
    },
    gatewayFooter: {
      location: 'Data Center • Pod A, Row 1',
      serial: 'X2M-DC-01001',
    },
    stats: [
      { label: 'Security', value: '100%', color: 'text-emerald-400' },
      { label: 'Alerts', value: '364', color: 'text-red-400' },
      { label: 'Uptime', value: '99.99%', color: 'text-cyan-400' },
      { label: 'Correlation Events', value: '12', color: 'text-orange-400', sub: 'CORR rules' },
    ],
    valueProps: [
      'OT + OOB networks monitored',
      'Palo Alto SASE at the edge',
      'Vision Agent for physical access',
      'Cyber-physical correlation',
    ],
    oemEdgeLabel: 'Event Telemetry',
    deviceEdgeLabel: 'Encrypted • Sovereign',
    legend: [
      { label: 'Security (Palo Alto)', type: 'dashed', color: COLORS.paloAlto },
      { label: 'AISentry™ Cloud', type: 'solid', color: '#F59E0B' },
      { label: 'Encrypted Link', type: 'solid', color: COLORS.secure },
      { label: 'Blocked (Air-Gap)', type: 'faded', color: COLORS.blocked },
    ],
  },
};

// ════════════════════════════════════════════════════════════════════════════
// ANIMATED GLOW RING
// ════════════════════════════════════════════════════════════════════════════
const GlowRing = ({ color, size = 100, delay = 0 }) => (
  <div
    className="absolute rounded-full animate-ping opacity-20"
    style={{
      width: size, height: size, backgroundColor: color,
      animationDelay: `${delay}ms`, animationDuration: '3s',
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    }}
  />
);

// ════════════════════════════════════════════════════════════════════════════
// DEVICE NODE — renders based on sector config
// ════════════════════════════════════════════════════════════════════════════
const DeviceIcons = {
  printer: (
    <svg className="w-10 h-10 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
    </svg>
  ),
  medical: (
    <svg className="w-10 h-10 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-4v-4H6v-4h4V6h4v4h4v4z"/>
    </svg>
  ),
  datacenter: (
    <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-7-2h.01M17 16h.01M7 8h.01M7 16h.01" />
    </svg>
  ),
};

const DeviceNode = ({ data }) => {
  const cfg = data.config;
  const compColors = {
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
    amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
    cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400' },
  };
  const cc = compColors[cfg.complianceColor] || compColors.emerald;

  return (
    <div className="relative group">
      <Handle type="target" position={Position.Top} className="!bg-cyan-400 !w-3 !h-3 !border-2 !border-slate-800" />

      <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 border border-slate-700/50 shadow-2xl min-w-[220px]"
           style={{ boxShadow: `0 0 40px ${data.glowColor || 'rgba(100,100,255,0.15)'}` }}>

        {/* Brand Badge */}
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full"
             style={{ backgroundColor: cfg.brandColor }}>
          <span className="text-[10px] font-bold text-white tracking-wider">{cfg.brand}</span>
        </div>

        {/* Status dot */}
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-slate-800 animate-pulse" />

        {/* Icon */}
        <div className="flex justify-center mb-3 mt-2">
          <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center border border-slate-600/50">
            {DeviceIcons[cfg.icon] || DeviceIcons.medical}
          </div>
        </div>

        {/* Info */}
        <div className="text-center space-y-1">
          <div className="text-sm font-bold text-white">{cfg.model}</div>
          <div className="text-xs" style={{ color: cfg.brandColor }}>{cfg.location}</div>
          <div className="text-[10px] text-slate-400">{cfg.facility}</div>
        </div>

        {/* Metrics */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {cfg.metrics.map(m => (
            <div key={m.label} className="bg-slate-800/50 rounded-lg p-2 text-center">
              <div className="text-[10px] text-slate-500 uppercase">{m.label}</div>
              <div className="text-sm font-bold text-emerald-400">{m.value}</div>
            </div>
          ))}
        </div>

        {/* Compliance Badge */}
        <div className={`mt-3 flex items-center justify-center gap-1 py-1.5 ${cc.bg} rounded-lg border ${cc.border}`}>
          <svg className={`w-3 h-3 ${cc.text}`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className={`text-[10px] font-medium ${cc.text}`}>{cfg.complianceBadge}</span>
        </div>

        {/* DC-specific: dual-agent swim lanes */}
        {cfg.type === 'dc' && (
          <div className="mt-2 space-y-1.5">
            <div className="py-1.5 px-2 bg-red-500/10 rounded border border-red-500/20">
              <div className="flex items-center justify-between">
                <div className="text-[8px] text-red-400 font-bold">DC Agent — OT Mode</div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-[7px] text-emerald-400">Active</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-0.5 mt-1">
                {['Modbus', 'BACnet', 'DNP3', 'SNMP'].map(p => (
                  <span key={p} className="px-1 py-0.5 bg-red-500/10 text-red-300 text-[6px] rounded border border-red-500/20">{p}</span>
                ))}
              </div>
              <div className="text-[7px] text-slate-500 mt-0.5">Facilities VLAN (Power · Cooling · UPS)</div>
            </div>
            <div className="py-1.5 px-2 bg-purple-500/10 rounded border border-purple-500/20">
              <div className="flex items-center justify-between">
                <div className="text-[8px] text-purple-400 font-bold">DC Agent — OOB Mode</div>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-[7px] text-emerald-400">Active</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-0.5 mt-1">
                {['IPMI v2', 'Redfish', 'iDRAC · iLO'].map(p => (
                  <span key={p} className="px-1 py-0.5 bg-purple-500/10 text-purple-300 text-[6px] rounded border border-purple-500/20">{p}</span>
                ))}
              </div>
              <div className="text-[7px] text-slate-500 mt-0.5">OOB Management VLAN (BMC · Hypervisors)</div>
            </div>
            <div className="text-[6px] text-slate-600 text-center italic mt-1">First agent to passively monitor OOB management — invisible to standard security tools</div>
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-cyan-400 !w-3 !h-3 !border-2 !border-slate-800" />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// X2M GATEWAY NODE — SASE architecture (no NGFW)
// ════════════════════════════════════════════════════════════════════════════
const X2MGatewayNode = ({ data }) => {
  const cfg = data.sectorConfig;

  return (
    <div className="relative group">
      <Handle type="target" position={Position.Top} className="!bg-orange-400 !w-3 !h-3 !border-2 !border-slate-800" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-orange-400 !w-3 !h-3 !border-2 !border-slate-800" />
      <Handle type="target" position={Position.Right} id="right" className="!bg-orange-400 !w-3 !h-3 !border-2 !border-slate-800" />

      <GlowRing color={COLORS.celerway} size={280} delay={0} />
      <GlowRing color={COLORS.paloAlto} size={260} delay={1000} />

      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border-2 border-orange-500/50 shadow-2xl overflow-hidden"
           style={{ boxShadow: `0 0 60px ${COLORS.celerwayLight}, 0 0 100px rgba(250, 88, 45, 0.1)`, minWidth: 340 }}>

        {/* Header Bar */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-5 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-sm">C</span>
              </div>
              <div>
                <div className="text-white font-bold text-sm tracking-wide">CELERWAY Xm</div>
                <div className="text-orange-200 text-[10px]">Sovereign Edge Gateway</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-[10px] text-white/80">ONLINE</span>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Palo Alto SASE Block */}
          <div className="relative bg-gradient-to-br from-red-950/50 to-slate-900 rounded-xl border border-red-500/30 p-4"
               style={{ boxShadow: 'inset 0 0 30px rgba(250, 88, 45, 0.1)' }}>

            <div className="absolute -top-2 left-4 px-2 py-0.5 bg-gradient-to-r from-red-600 to-orange-600 rounded text-[9px] font-bold text-white">
              PALO ALTO NETWORKS
            </div>

            <div className="mt-2 flex items-start gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-red-600/20 to-orange-600/20 rounded-xl flex items-center justify-center border border-red-500/30 flex-shrink-0">
                <svg className="w-8 h-8 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold text-sm">Prisma SASE</div>
                <div className="text-slate-400 text-xs mt-0.5">Cloud Security Service • Threat Prevention</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {['Zero Trust', 'Prisma Access', 'Threat Prev', 'Data Loss Prev'].map(feature => (
                    <span key={feature} className="px-1.5 py-0.5 bg-red-500/10 text-red-400 text-[8px] rounded border border-red-500/20">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Threat Stats */}
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">BLOCKED</div>
                <div className="text-sm font-bold text-red-400">2.4K</div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">INSPECTED</div>
                <div className="text-sm font-bold text-orange-400">847K</div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-2 text-center">
                <div className="text-[9px] text-slate-500">ENCRYPTED</div>
                <div className="text-sm font-bold text-emerald-400">100%</div>
              </div>
            </div>
          </div>

          {/* AI Sentry Agent Block (Tier 2+ only) */}
          {cfg && cfg.device && cfg.device.type !== 'printer-basic' && (
            <div className="relative bg-gradient-to-br from-blue-950/50 to-slate-900 rounded-xl border border-blue-500/30 p-3">
              <div className="absolute -top-2 left-4 px-2 py-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded text-[9px] font-bold text-white">
                AISENTRY™
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
                  <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-white font-semibold text-xs">Deep Packet Inspection</div>
                  <div className="flex gap-1 mt-1">
                    {(cfg.device.type === 'dc'
                      ? ['Modbus', 'BACnet', 'SNMP', 'IPMI', 'Redfish']
                      : cfg.device.type === 'medical'
                      ? ['Modbus', 'BACnet', 'DICOM', 'HL7', 'SNMP']
                      : ['Modbus', 'BACnet', 'SNMP', 'JetDirect', 'IPP']
                    ).map(p => (
                      <span key={p} className="px-1 py-0.5 bg-blue-500/10 text-blue-400 text-[7px] rounded border border-blue-500/20">{p}</span>
                    ))}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-[9px] text-emerald-400">Active</span>
                </div>
              </div>
            </div>
          )}

          {/* Connectivity Stack */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-cyan-500/20 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                </div>
                <span className="text-xs font-medium text-white">5G/LTE</span>
              </div>
              <div className="text-[10px] text-slate-400">Multi-carrier bonding</div>
              <div className="text-xs font-bold text-cyan-400 mt-1">↑ 85 Mbps</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-emerald-500/20 rounded flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                </div>
                <span className="text-xs font-medium text-white">IPSec VPN</span>
              </div>
              <div className="text-[10px] text-slate-400">AES-256-GCM</div>
              <div className="text-xs font-bold text-emerald-400 mt-1">● Active</div>
            </div>
          </div>

          {/* Air Gap */}
          <div className="flex items-center gap-2 py-2 px-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
            <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <div className="text-xs font-semibold text-emerald-400">{cfg?.airGap?.title || 'Network-Isolated'}</div>
              <div className="text-[10px] text-emerald-400/70">{cfg?.airGap?.subtitle || 'Traffic never touches local network'}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-slate-900/50 border-t border-slate-700/50">
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-slate-500">{cfg?.gatewayFooter?.location || ''}</div>
            <span className="text-[10px] text-slate-400">Serial: {cfg?.gatewayFooter?.serial || 'X2M-000'}</span>
          </div>
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-orange-400 !w-3 !h-3 !border-2 !border-slate-800" />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PALO ALTO CLOUD NODE
// ════════════════════════════════════════════════════════════════════════════
const PaloAltoCloudNode = ({ data }) => (
  <div className="relative group">
    <Handle type="target" position={Position.Bottom} className="!bg-red-400 !w-3 !h-3 !border-2 !border-slate-800" />
    <GlowRing color={COLORS.paloAlto} size={180} delay={500} />
    <div className="relative bg-gradient-to-br from-red-950 via-slate-900 to-slate-900 rounded-2xl border border-red-500/40 shadow-2xl overflow-hidden min-w-[260px]"
         style={{ boxShadow: 'rgba(250, 88, 45, 0.15) 0px 0px 50px' }}>
      <div className="bg-gradient-to-r from-red-700 to-orange-600 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-lg">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#FA582D"/>
              <path d="M2 17l10 5 10-5" stroke="#FA582D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12l10 5 10-5" stroke="#FA582D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <div className="text-white font-bold">Cortex XSOAR</div>
            <div className="text-red-200 text-xs">Security Event Integration</div>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-2">
        {[
          { name: 'AISentry\u2122 \u2192 Cortex Event Push', icon: '🔗' },
          { name: 'Native Cortex Event Format', icon: '📋' },
          { name: 'Automated Response Playbooks', icon: '⚡' },
        ].map(service => (
          <div key={service.name} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm">{service.icon}</span>
              <span className="text-xs text-white">{service.name}</span>
            </div>
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </div>
        ))}
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="down" className="!bg-red-400 !w-3 !h-3 !border-2 !border-slate-800" />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// OEM CLOUD NODE — parameterized by sector
// ════════════════════════════════════════════════════════════════════════════
const OEMCloudNode = ({ data }) => {
  const cfg = data.config;
  return (
    <div className="relative group">
      <Handle type="target" position={Position.Bottom} className="!w-3 !h-3 !border-2 !border-slate-800" style={{ backgroundColor: cfg.color }} />
      <GlowRing color={cfg.color} size={180} delay={800} />
      <div className={`relative bg-gradient-to-br ${cfg.bgFrom} via-slate-900 to-slate-900 rounded-2xl ${cfg.borderColor} border shadow-2xl overflow-hidden min-w-[260px]`}
           style={{ boxShadow: `0 0 50px ${cfg.colorLight}` }}>
        <div className={`bg-gradient-to-r ${cfg.gradientFrom} ${cfg.gradientTo} px-4 py-3`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-2xl">{cfg.icon}</span>
            </div>
            <div>
              <div className="text-white font-bold">{cfg.name}</div>
              <div className="text-white/70 text-xs">{cfg.subtitle}</div>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {cfg.stats && (
            <div className="grid grid-cols-2 gap-2">
              {cfg.stats.map(s => (
                <div key={s.label} className="bg-slate-800/50 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-slate-500 uppercase">{s.label}</div>
                  <div className="text-lg font-bold" style={{ color: cfg.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {cfg.services.map(service => (
              <div key={service.name} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-2">
                <span className="text-sm">{service.icon}</span>
                <span className="text-xs text-white">{service.name}</span>
                <div className="ml-auto w-2 h-2 bg-emerald-400 rounded-full" />
              </div>
            ))}
          </div>
          {cfg.badge && (
            <div className="flex items-center gap-2 py-2 px-3 rounded-lg border" style={{ backgroundColor: `${cfg.color}15`, borderColor: `${cfg.color}30` }}>
              <span className="text-sm">{cfg.badge.icon}</span>
              <span className="text-xs" style={{ color: cfg.color }}>{cfg.badge.text}</span>
            </div>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="down" className="!w-3 !h-3 !border-2 !border-slate-800" style={{ backgroundColor: cfg.color }} />
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// BLOCKED LAN NODE — parameterized
// ════════════════════════════════════════════════════════════════════════════
const BlockedLANNode = ({ data }) => (
  <div className="relative opacity-80">
    <Handle type="target" position={Position.Left} className="!bg-red-400 !w-3 !h-3 !border-2 !border-slate-800" />
    <div className="relative bg-gradient-to-br from-red-950/50 to-slate-800/80 rounded-xl border-2 border-red-500/40 p-4 min-w-[180px]"
         style={{ boxShadow: '0 0 30px rgba(239, 68, 68, 0.2)' }}>
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center border-2 border-slate-800 shadow-lg">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center border border-red-500/50">
          <svg className="w-6 h-6 text-red-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
          </svg>
        </div>
        <div>
          <div className="text-sm font-semibold text-red-300">{data.name || 'Facility LAN'}</div>
          <div className="text-[10px] text-slate-400">{data.subtitle || 'IT Network'}</div>
        </div>
      </div>
      <div className="mt-3 text-center py-2 bg-red-500/20 rounded-lg border border-red-500/40">
        <span className="text-[10px] font-bold text-red-300">{data.status || 'NO ACCESS • ISOLATED'}</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// NODE TYPES
// ════════════════════════════════════════════════════════════════════════════
const nodeTypes = {
  device: DeviceNode,
  x2mGateway: X2MGatewayNode,
  paloAltoCloud: PaloAltoCloudNode,
  oemCloud: OEMCloudNode,
  blockedLAN: BlockedLANNode,
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR — parameterized
// ════════════════════════════════════════════════════════════════════════════
const StatsBar = ({ stats }) => (
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-6 bg-slate-800/80 backdrop-blur-sm rounded-xl px-5 py-3 border border-slate-700/50">
      {stats.map((s, i) => (
        <React.Fragment key={s.label}>
          {i > 0 && <div className="w-px h-8 bg-slate-700" />}
          <div className="text-center">
            <div className="text-[10px] text-slate-500 uppercase">{s.label}</div>
            <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
            {s.sub && <div className="text-[8px] text-slate-500">{s.sub}</div>}
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — accepts sector prop
// ════════════════════════════════════════════════════════════════════════════
const SovereignEdgeTopology = ({ sector = 'print' }) => {
  const cfg = SECTOR_CONFIGS[sector] || SECTOR_CONFIGS.print;
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  const buildTopology = useCallback(() => {
    const CENTER_X = 500;

    const newNodes = [
      {
        id: 'palo-cloud',
        type: 'paloAltoCloud',
        position: { x: CENTER_X - 280, y: 0 },
        data: {},
      },
      {
        id: 'oem-cloud',
        type: 'oemCloud',
        position: { x: CENTER_X + 120, y: 0 },
        data: { config: cfg.oemCloud },
      },
      {
        id: 'x2m-gateway',
        type: 'x2mGateway',
        position: { x: CENTER_X - 70, y: 220 },
        data: { sectorConfig: cfg },
      },
      {
        id: 'facility-lan',
        type: 'blockedLAN',
        position: { x: CENTER_X - 350, y: 320 },
        data: cfg.facilityLAN,
      },
      {
        id: 'device-primary',
        type: 'device',
        position: { x: CENTER_X - 10, y: 580 },
        data: {
          config: cfg.device,
          glowColor: cfg.accentLight,
        },
      },
    ];

    const newEdges = [
      {
        id: 'palo-to-x2m',
        source: 'palo-cloud',
        target: 'x2m-gateway',
        targetHandle: 'left',
        type: 'smoothstep',
        animated: true,
        style: { stroke: COLORS.paloAlto, strokeWidth: 3, strokeDasharray: '5,5' },
        label: 'Event Push \u2192',
        labelStyle: { fill: COLORS.paloAlto, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: COLORS.darkAlt, fillOpacity: 0.9 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      {
        id: 'oem-to-x2m',
        source: 'oem-cloud',
        target: 'x2m-gateway',
        targetHandle: 'right',
        type: 'smoothstep',
        animated: true,
        style: { stroke: cfg.accentColor, strokeWidth: 3 },
        label: cfg.oemEdgeLabel,
        labelStyle: { fill: cfg.accentColor, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: COLORS.darkAlt, fillOpacity: 0.9 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      {
        id: 'x2m-to-device',
        source: 'x2m-gateway',
        target: 'device-primary',
        type: 'smoothstep',
        animated: true,
        style: { stroke: COLORS.secure, strokeWidth: 3 },
        label: cfg.deviceEdgeLabel,
        labelStyle: { fill: COLORS.secure, fontSize: 10, fontWeight: 600 },
        labelBgStyle: { fill: COLORS.darkAlt, fillOpacity: 0.9 },
        labelBgPadding: [6, 4],
        labelBgBorderRadius: 4,
      },
      {
        id: 'blocked-lan',
        source: 'x2m-gateway',
        target: 'facility-lan',
        sourceHandle: 'left',
        type: 'smoothstep',
        animated: false,
        style: { stroke: COLORS.blocked, strokeWidth: 2, strokeDasharray: '8,8', opacity: 0.4 },
      },
    ];

    return { nodes: newNodes, edges: newEdges };
  }, [cfg]);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const { nodes: n, edges: e } = buildTopology();
      setNodes(n);
      setEdges(e);
      setLoading(false);
    }, 1200);
  }, [buildTopology, setNodes, setEdges]);

  const badgeColorMap = {
    orange: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    red: 'bg-red-500/20 text-red-300 border-red-500/30',
    amber: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    purple: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  };

  return (
    <div className="h-screen w-full bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,107,53,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-950/98 to-transparent pb-12 px-6 pt-5">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {cfg.title}
            </h1>
            <p className="text-slate-400 text-sm mt-1.5">{cfg.subtitle}</p>
            <div className="mt-3 flex items-center gap-2">
              {cfg.badges.map((badge, i) => (
                <span key={badge} className={`text-[10px] px-2.5 py-1 rounded-full border font-medium ${badgeColorMap[cfg.badgeColors[i]] || badgeColorMap.emerald}`}>
                  {badge}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <StatsBar stats={cfg.stats} />
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-950/95">
          <div className="text-center">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-orange-500/30 rounded-full" />
              <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
              <div className="absolute inset-2 border-4 border-red-500/30 rounded-full" />
              <div className="absolute inset-2 border-4 border-red-500 border-t-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
            </div>
            <div className="text-orange-400 text-lg font-semibold">Initializing Sovereign Edge</div>
            <div className="text-slate-500 text-sm mt-1">Establishing secure tunnel...</div>
          </div>
        </div>
      )}

      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 0.75 }}
          minZoom={0.25}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.55 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={30} size={1} color="rgba(255, 107, 53, 0.08)" />
          <Controls className="!bg-slate-800/90 !border-slate-700/50 !rounded-xl" />

          {/* Legend Panel */}
          <Panel position="bottom-left" className="!bottom-8 !left-4">
            <div className="bg-slate-900/95 backdrop-blur-sm rounded-xl p-3 border border-slate-700/50 space-y-2 max-w-[200px]">
              <div className="text-[10px] font-semibold text-slate-200 uppercase tracking-wider">Data Flow</div>
              <div className="space-y-1.5">
                {cfg.legend.map(l => (
                  <div key={l.label} className="flex items-center gap-2">
                    <div className="w-5 h-0.5 rounded" style={{
                      backgroundColor: l.color,
                      opacity: l.type === 'faded' ? 0.4 : 1,
                      backgroundImage: l.type === 'dashed'
                        ? `repeating-linear-gradient(90deg, ${l.color}, ${l.color} 4px, transparent 4px, transparent 8px)`
                        : undefined,
                      backgroundColor: l.type === 'dashed' ? 'transparent' : l.color,
                    }} />
                    <span className="text-[10px] text-slate-300">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          {/* Value Prop Panel */}
          <Panel position="bottom-right" className="!bottom-8 !right-4">
            <div className="bg-slate-900/95 backdrop-blur-sm rounded-xl p-3 border border-slate-700/50 max-w-[220px]">
              <div className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider mb-2">Sovereign Edge Value</div>
              <div className="space-y-1.5 text-[10px] text-slate-300">
                {cfg.valueProps.map(vp => (
                  <div key={vp} className="flex items-start gap-1.5">
                    <span className="text-emerald-400">✓</span>
                    <span>{vp}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </ReactFlow>
      )}

      {/* Animations */}
      <style>{`
        @keyframes ping {
          75%, 100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        .animate-ping { animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite; }
      `}</style>
    </div>
  );
};

export default SovereignEdgeTopology;

// ════════════════════════════════════════════════════════════════════════════
// ROUTE HELPER COMPONENTS — drop-in replacements for your router
// ════════════════════════════════════════════════════════════════════════════
// Usage in App.js / routes:
//   <Route path="/sovereign-edge" element={<SovereignEdgePrint />} />
//   <Route path="/sovereign-edge-medical" element={<SovereignEdgeMedical />} />
//   <Route path="/sovereign-edge-dc" element={<SovereignEdgeDC />} />

export const SovereignEdgePrint = () => <SovereignEdgeTopology sector="print" />;
export const SovereignEdgeMedical = () => <SovereignEdgeTopology sector="medical" />;
export const SovereignEdgeDC = () => <SovereignEdgeTopology sector="dc" />;
